<template>
  <div>
    <CRow>
      <CCol col="12">
        <CCard class="chat-card">
          <CCardHeader class="chat-header">
            <div class="header-content">
              <div class="d-flex align-items-center">
                <div class="support-avatar">
                  <img src="/img/panel_icon.png" alt="Support" @error="handleImageError">
                  <span class="online-status"></span>
                </div>
                <div class="header-info">
                  <h5 class="mb-0">پشتیبانی آنلاین</h5>
                  <small class="text-muted">
                    <span class="status-text">آنلاین</span>
                    <span class="dot-separator">•</span>
                    <span>پاسخگویی سریع</span>
                  </small>
                </div>
              </div>
              <div class="header-actions">
                <CButton color="light" size="sm" @click="refreshMessages" class="btn-icon">
                  <!-- Refresh SVG Icon -->
                  <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      :class="{'spin': loading}"
                  >
                    <path d="M23 4v6h-6" />
                    <path d="M1 20v-6h6" />
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
                    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
                  </svg>
                </CButton>
              </div>
            </div>
          </CCardHeader>

          <CCardBody class="chat-body">
            <div class="message-container" id="message_support_root" ref="messageContainer">
              <div v-if="message_list.length === 0" class="empty-state">
                <!-- Comment SVG Icon -->
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <p>هیچ پیامی وجود ندارد</p>
                <small>پیام خود را ارسال کنید</small>
              </div>

              <div v-for="(msg, index) in message_list" :key="index" class="message-wrapper">
                <div :class="`message-bubble ${msg.from_id == null ? 'support' : 'user'}`">
                  <div class="message-content">
                    <div class="message-header">
                      <div class="sender-info">
                        <span class="sender-name" v-if="msg.from_id == null">
                          <!-- Headphones SVG -->
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                            <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z" />
                            <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                          </svg>
                          پشتیبانی
                        </span>
                        <span class="sender-name" v-if="msg.from_id != null">
                          <!-- User SVG -->
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                            <circle cx="12" cy="7" r="4" />
                          </svg>
                          شما
                        </span>
                      </div>
                      <span class="message-time">
                        {{ get_date_time(msg.created_at) }}
                      </span>
                    </div>
                    <p class="message-text">{{ msg.message }}</p>
                  </div>
                  <div class="message-avatar" v-if="msg.from_id == null">
                    <img src="/img/panel_icon.png" alt="Support" @error="handleImageError">
                  </div>
                  <div class="message-avatar user-avatar" v-else>
                    <img :src="user_image || defaultUserImage" @error="handleUserImageError">
                  </div>
                </div>
              </div>
            </div>
          </CCardBody>

          <CCardFooter class="chat-footer">
            <div class="chat-input-wrapper">
              <CInput
                  class="chat-input"
                  placeholder="پیام خود را بنویسید..."
                  v-model="message"
                  @keyup.enter="send_message()"
                  :disabled="sending"
              >
                <template #append>
                  <CButton
                      @click="send_message()"
                      color="primary"
                      class="send-btn"
                      :disabled="!message.trim() || sending"
                  >
                    <!-- Send SVG -->
                    <svg
                        v-if="!sending"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>

                    <!-- Spinner SVG -->
                    <svg
                        v-else
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="spin"
                    >
                      <line x1="12" y1="2" x2="12" y2="6" />
                      <line x1="12" y1="18" x2="12" y2="22" />
                      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
                      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
                      <line x1="2" y1="12" x2="6" y2="12" />
                      <line x1="18" y1="12" x2="22" y2="12" />
                      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
                      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
                    </svg>

                    <span class="btn-text">ارسال</span>
                  </CButton>
                </template>
              </CInput>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import axios from "axios";
import { bus } from '../../main';

export default {
  name: 'SupportChat',
  data() {
    return {
      user_image: '',
      defaultUserImage: '/images/default-user.png',
      message: '',
      message_list: [],
      sending: false,
      loading: false,
    }
  },
  mounted() {
    this.user_image = localStorage.getItem('image') || this.defaultUserImage;
    this.get_messages();
    this.setupAutoScroll();
  },
  watch: {
    message_list: {
      deep: true,
      handler() {
        this.scrollToBottom();
      }
    }
  },
  methods: {
    setupAutoScroll() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    handleImageError(e) {
      e.target.style.display = 'none';
      const parent = e.target.parentElement;
      if (parent) {
        // Show fallback SVG instead of creating element
        const fallback = document.createElement('div');
        fallback.className = 'fallback-avatar';
        fallback.innerHTML = `
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        `;
        parent.appendChild(fallback);
      }
    },

    handleUserImageError(e) {
      // If user image fails, show default
      e.target.src = this.defaultUserImage;
    },

    refreshMessages() {
      this.loading = true;
      this.get_messages();
      setTimeout(() => {
        this.loading = false;
      }, 1000);
    },

    get_messages() {
      var self = this;
      var formData = new FormData();

      axios.post('/api/admin/site/get_admin_messages', formData, {})
          .then(function (response) {
            self.message_list = response.data;
            self.scrollToBottom();
            bus.$emit('update_unread_message', {});
          })
          .catch(function (error) {
            console.log(error);
          });
    },

    send_message() {
      if (!this.message.trim() || this.sending) {
        return false;
      }

      this.sending = true;
      var self = this;
      var formData = new FormData();

      formData.append('message', this.message);

      axios.post('/api/admin/site/send_admin_messages', formData, {})
          .then(function (response) {
            if (response.data.error == 0) {
              self.message_list.push({
                message: self.message,
                to_id: null,
                from_id: 0,
                created_at: new Date(),
                updated_at: new Date(),
              });

              self.message = '';
              self.scrollToBottom();
            }
            self.sending = false;
          })
          .catch(function (error) {
            console.log(error);
            self.sending = false;
          });
    },

    get_date_time(date) {
      return new Date(date).toLocaleString('fa-IR');
    }
  }
}
</script>

<style scoped>
/* Chat Container */
.chat-card {
  border-radius: 12px;
  box-shadow: 0 2px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
}

/* Header */
.chat-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 16px 24px;
  border-bottom: none;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.support-avatar {
  position: relative;
  width: 44px;
  height: 44px;
  margin-left: 12px;
  flex-shrink: 0;
}

.support-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.online-status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background: #4caf50;
  border-radius: 50%;
  border: 2px solid #667eea;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.header-info {
  color: white;
}

.header-info h5 {
  font-weight: 600;
  margin-bottom: 2px;
}

.header-info small {
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
}

.status-text {
  color: #4caf50;
}

.dot-separator {
  margin: 0 6px;
}

.header-actions .btn-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  transition: all 0.3s ease;
}

.header-actions .btn-icon:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(30deg);
}

/* Body */
.chat-body {
  flex: 1;
  padding: 0;
  background: #f8f9fa;
  overflow: hidden;
}

.message-container {
  height: 100%;
  overflow-y: auto;
  padding: 20px 24px;
}

.message-container::-webkit-scrollbar {
  width: 6px;
}

.message-container::-webkit-scrollbar-track {
  background: transparent;
}

.message-container::-webkit-scrollbar-thumb {
  background: #c1c7cd;
  border-radius: 3px;
}

.message-container::-webkit-scrollbar-thumb:hover {
  background: #a0a7ae;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #adb5bd;
}

.empty-state svg {
  color: #dee2e6;
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 18px;
  margin-bottom: 8px;
  color: #6c757d;
}

.empty-state small {
  color: #adb5bd;
}

/* Message Wrapper */
.message-wrapper {
  margin-bottom: 16px;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Message Bubble */
.message-bubble {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 80%;
}

.message-bubble.support {
  flex-direction: row;
}

.message-bubble.user {
  flex-direction: row-reverse;
  margin-right: auto;
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
}

.message-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.message-avatar.user-avatar {
  background: #667eea;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.message-avatar.user-avatar img {
  border: 2px solid rgba(255, 255, 255, 0.3);
}

.fallback-avatar {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9ecef;
  color: #6c757d;
  border-radius: 50%;
}

.message-content {
  background: white;
  padding: 12px 16px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  flex: 1;
}

.message-bubble.support .message-content {
  background: white;
  border-bottom-left-radius: 4px;
}

.message-bubble.user .message-content {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
  font-size: 12px;
}

.sender-name {
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  gap: 4px;
}

.sender-name svg {
  width: 14px;
  height: 14px;
}

.message-bubble.user .sender-name {
  color: rgba(255, 255, 255, 0.9);
}

.message-time {
  font-size: 11px;
  color: #adb5bd;
}

.message-bubble.user .message-time {
  color: rgba(255, 255, 255, 0.7);
}

.message-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
}

.message-bubble.user .message-text {
  color: white;
}

/* Footer */
.chat-footer {
  background: white;
  padding: 12px 20px;
  border-top: 1px solid #e9ecef;
  flex-shrink: 0;
}

.chat-input-wrapper {
  max-width: 100%;
}

.chat-input {
  border: none;
  background: #f8f9fa;
  border-radius: 25px;
}

.chat-input >>> .form-control {
  border-radius: 25px !important;
  border: 1px solid #e9ecef !important;
  padding: 10px 20px !important;
  background: #f8f9fa !important;
  font-size: 14px;
}

.chat-input >>> .form-control:focus {
  border-color: #667eea !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
}

.chat-input >>> .input-group-append {
  margin-right: -1px;
}

.send-btn {
  border-radius: 25px !important;
  padding: 8px 20px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.send-btn svg {
  width: 16px;
  height: 16px;
  stroke: white;
}

.send-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.send-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-text {
  margin-right: 4px;
}

/* Spin Animation */
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .message-bubble {
    max-width: 90%;
  }

  .chat-card {
    height: calc(100vh - 80px);
  }

  .header-info h5 {
    font-size: 16px;
  }

  .btn-text {
    display: none;
  }

  .send-btn {
    padding: 8px 16px !important;
  }
}
</style>